import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TONE, fmtMoney, toneColor } from '../shared';

export interface ISaldoBlockProps {
  /** Block heading (e.g. «Итог за период» or «Сальдо»). */
  title: string;
  /** Optional sub-caption — the day/week/period label. */
  caption?: string;
  /** Balance on the account at the start of the scope. */
  opening: number;
  /** Planned income arriving in the scope (the «+» line). */
  income: number;
  /** Of that income, how much is already overdue (a risk note). */
  overdue: number;
  /** Planned spending in the scope (the «−» line). */
  expense: number;
  /** Resulting balance = opening + income − expense. */
  closing: number;
  /** Already-received money (informational, NOT part of the forecast math). */
  received?: number;
  /**
   * Worst balance reached inside the scope (min end-balance). Drives the
   * «хватает / не хватает» verdict so an intra-period dip is caught even when
   * the closing balance ends positive. Falls back to `closing`.
   */
  minBalance?: number | null;
}

/**
 * The cash «saldo» waterfall for a day, a week, or the whole studied period:
 *
 *   Остаток на начало        12 000
 *   + Поступит по плану      + 6 500   (из них просрочено 2 000)
 *   − Потратить по плану     − 3 000
 *   ─────────────────────
 *   = Останется              15 500   ✓ Хватает
 *
 * Makes the cash position legible at a glance and answers the CFO's core
 * question — does the money hold up? When part of the income is overdue, a
 * pessimistic «without overdue» line shows what is left if it never arrives.
 */
export const SaldoBlock = memo<ISaldoBlockProps>(
  ({
    title,
    caption,
    opening,
    income,
    overdue,
    expense,
    closing,
    received = 0,
    minBalance = null,
  }) => {
    const { t } = useTranslation();
    const worst = minBalance != null ? minBalance : closing;
    const ok = worst >= 0;
    const withoutOverdue = closing - overdue;

    return (
      <Block>
        <Header>
          <span className="sb-title">{title}</span>
          {caption ? <span className="sb-caption">{caption}</span> : null}
        </Header>

        <Row>
          <span className="sb-label">
            {t('pages.paymentCalendar.saldo.opening')}
          </span>
          <strong>{fmtMoney(opening)}</strong>
        </Row>
        <Row>
          <span
            className="sb-label"
            style={{ color: income < 0 ? TONE.danger : TONE.ok }}
          >
            {income < 0 ? '−' : '+'} {t('pages.paymentCalendar.saldo.income')}
          </span>
          {/* Sign/color follow the actual value so a (defensive) negative
              income never renders as a green «+ −300». */}
          <strong style={{ color: income < 0 ? TONE.danger : TONE.ok }}>
            {income < 0 ? '−' : '+'} {fmtMoney(Math.abs(income))}
          </strong>
        </Row>
        {overdue > 0 && (
          <SubRow>
            {t('pages.paymentCalendar.saldo.overdueOf')}:{' '}
            <span style={{ color: TONE.danger }}>{fmtMoney(overdue)}</span>
          </SubRow>
        )}
        <Row>
          <span className="sb-label" style={{ color: TONE.danger }}>
            − {t('pages.paymentCalendar.saldo.expense')}
          </span>
          <strong style={{ color: TONE.danger }}>− {fmtMoney(expense)}</strong>
        </Row>

        <Divider />

        <Row className="sb-total">
          <span className="sb-label">
            {t('pages.paymentCalendar.saldo.closing')}
          </span>
          <strong style={{ color: toneColor(closing) }}>
            {fmtMoney(closing)}
          </strong>
        </Row>

        <Verdict ok={ok}>
          {ok
            ? t('pages.paymentCalendar.saldo.enough')
            : `${t('pages.paymentCalendar.saldo.shortfall')} · ${t(
                'pages.paymentCalendar.saldo.min',
              )} ${fmtMoney(worst)}`}
        </Verdict>

        {overdue > 0 && (
          <Note style={{ color: withoutOverdue < 0 ? TONE.danger : '#94a3b8' }}>
            {t('pages.paymentCalendar.saldo.ifOverdueMissing')}{' '}
            {fmtMoney(withoutOverdue)}
          </Note>
        )}
        {received > 0 && (
          <Note>
            {t('pages.paymentCalendar.saldo.received')}: {fmtMoney(received)}
          </Note>
        )}
      </Block>
    );
  },
);

/* ----------------------------- styles ----------------------------- */

const Block = styled.div`
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  font-size: 13px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;

  .sb-title {
    font-weight: 700;
    font-size: 14px;
    color: #0f172a;
  }
  .sb-caption {
    color: #94a3b8;
    font-size: 12px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  font-variant-numeric: tabular-nums;

  .sb-label {
    color: #595959;
  }
  &.sb-total {
    font-size: 15px;
    .sb-label {
      color: #0f172a;
      font-weight: 600;
    }
    strong {
      font-weight: 700;
    }
  }
`;

const SubRow = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin: -2px 0 6px 12px;
  font-variant-numeric: tabular-nums;
`;

const Divider = styled.div`
  height: 1px;
  background: #ededed;
  margin: 8px 0;
`;

const Verdict = styled.div<{ ok: boolean }>`
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  text-align: center;
  color: #fff;
  background: ${({ ok }) => (ok ? TONE.ok : TONE.danger)};
`;

const Note = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
`;
